import React, { useState } from "react";
import theme from "assets/theme";
import { motion, useAnimation } from "framer-motion";
// Styled Components
import { StyledTestimonialsHeaderBox, StyledTestimonialsSectionContainer, StyledTestimonialsTextBox } from "./index.styles";
import HeaderContainer from "styles/components/headerContainer";
// Components
import { Stack } from "@mui/material";
import MKTypography from "components/MKTypography";
import SliderNavigation from "components/SliderNavigation";
// Utils
import useMediaQueries from "utils/mediaQueries.utils";
// Data
import mockReviewData from "./mockReviewData.json";
import mockReviewDataOnlineTherapy from "./mockReviewDataOnlineTherapy.json";
// ! Uncomment to add animations (removed animations for now)
// import withSectionAnimation from "styles/animations/components/withSectionAnimation";
// import { fadeInReverseVariants } from "styles/animations/sectionFadeIn.animations";

// const AnimatedStyledTestimonialsSectionContainer = withSectionAnimation(StyledTestimonialsSectionContainer, fadeInReverseVariants);

const Testimonials = ({
  cssProps,
  title,
  description,
  titleCssProps,
  showIcon = true,
  isOnlineTherapy = false,
  highlightText = ["Real Results"],
  highlightColor = theme.palette.accent.main,
}) => {
  const [currentIndex, setCurrentIndex] = useState(1);
  const { isMd, isLg } = useMediaQueries();
  const controls = useAnimation();
  const currentReviewData = isOnlineTherapy ? mockReviewDataOnlineTherapy : mockReviewData;

  const getCardData = (offset) => {
    const index = (currentIndex + offset + currentReviewData.length) % currentReviewData.length;
    return currentReviewData[index] || currentReviewData[0];
  };

  const handlePrev = async () => {
    await controls.start({ scale: 0.8 });
    await controls.start({ x: 50, opacity: 0 });
    setCurrentIndex((prevIndex) => (prevIndex - 1 + currentReviewData.length) % currentReviewData.length);
    controls.set({ x: -50 });
    await controls.start({ x: 0, opacity: 1, scale: 1 });
  };

  const handleNext = async () => {
    await controls.start({ scale: 0.8 });
    await controls.start({ x: -50, opacity: 0 });
    setCurrentIndex((prevIndex) => (prevIndex + 1) % currentReviewData.length);
    controls.set({ x: 50 });
    await controls.start({ x: 0, opacity: 1, scale: 1 });
  };

  const handleSelect = async (newIndex, currentIndex) => {
    if (newIndex === currentIndex) return;
    await controls.start({ scale: 0.8 });
    if (newIndex < currentIndex) {
      await controls.start({ x: 50, opacity: 0 });
      controls.set({ x: -50 });
    } else {
      await controls.start({ x: -50, opacity: 0 });
      controls.set({ x: 50 });
    }
    setCurrentIndex(newIndex);
    await controls.start({ x: 0, opacity: 1, scale: 1 });
  };

  // todo: do we need this anymore? Might be [deprecated]
  const headerStyleProps = {
    "&::before": {
      content: '""',
      display: "block",
      backgroundImage: "url('https://almadelic-assets.s3.us-east-2.amazonaws.com/logos/almadelicButterFlyTransparent.webp')",
      backgroundSize: "contain",
      backgroundRepeat: "no-repeat",
      width: "145px",
      height: "105px",
      margin: "auto",
    },
  };
  return (
    <StyledTestimonialsSectionContainer component="section" id="testimonials-section">
      <StyledTestimonialsHeaderBox>
        <HeaderContainer
          title={title ? title : "Real People, Real Results"}
          highlightText={highlightText ? highlightText : ["Real Results"]}
          textColor={cssProps ? cssProps.color : theme.palette.primary.main}
          highlightColor={highlightColor ? highlightColor : theme.palette.accent.main}
          component="h2"
          variant="h2"
          cssProps={{
            margin: "0rem 0rem 1rem 0rem !important",
            ...(showIcon === true && headerStyleProps),
          }}
        />
        <MKTypography sx={{ fontWeight: "300", color: "#000 !important", fontSize: "90%" }} variant="p">
          <em>
            {description ? (
              description
            ) : (
              <>
                {" "}
                Transforming Lives Through the Power of Ketamine Psychedelic Therapy.
                {isMd && <br />} Hear from Our Community
              </>
            )}
          </em>
        </MKTypography>
      </StyledTestimonialsHeaderBox>

      <Stack
        component={motion.div}
        animate={controls}
        initial={{ opacity: 1, x: 0, scale: 1 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        direction="row"
        sx={{ justifyContent: "center" }}
      >
        {[-1, 0, 1].map((offset) => (
          <StyledTestimonialsTextBox
            key={offset}
            sx={{
              scale: offset === 0 ? 1 : 0.8,
              boxShadow:
                offset === 0
                  ? "rgba(0, 0, 0, 0.07) 0px 1px 2px, rgba(0, 0, 0, 0.07) 0px 2px 4px, rgba(0, 0, 0, 0.07) 0px 4px 8px, rgba(0, 0, 0, 0.07) 0px 8px 16px, rgba(0, 0, 0, 0.07) 0px 16px 32px, rgba(0, 0, 0, 0.07) 0px 32px 64px"
                  : "rgba(0,0,0,0.2) 0px 0px 10px 0px",
              opacity: offset === 0 ? 1 : 0.5,
            }}
          >
            <MKTypography component="p" variant={isLg ? "body1" : "body2"}>
              "{getCardData(offset).review}"
            </MKTypography>
            <MKTypography component="p" variant="h6">
              - {getCardData(offset).name}
            </MKTypography>
          </StyledTestimonialsTextBox>
        ))}
      </Stack>

      <SliderNavigation
        onSelect={handleSelect}
        onPrevious={handlePrev}
        onNext={handleNext}
        currentIndex={currentIndex}
        totalItems={currentReviewData.length}
        showLeftButton={currentIndex !== 0}
        showRightButton={currentIndex !== currentReviewData.length - 1}
        paginationDots={{
          type: "linear",
          small: false,
          totalDots: 3, // how did we decide this?  What if there are more than 3 items?
        }}
      />
    </StyledTestimonialsSectionContainer>
  );
};

export default Testimonials;
